import { getValue, fetchAndActivate, Value } from 'firebase/remote-config';
import { firebaseRemoteConfigApp } from '../auth/firebaseConfig';
import { applyLocalOverrides } from './firebaseRemoteConfig.local';

// Define the structure of your remote config
interface RemoteConfigParams {
  isAttachmentEnabledInChat: boolean;
  welcomeMessage: string;
  maxListingsPerUser: number;
  isComingSoonEnabled: boolean;
  isFeedbackEnabled: boolean;
  requireEmailVerificationToCreateListing: boolean;
  requireIdentityVerification: boolean;
  isGroupedListingEnabled: boolean;
  isAutoApplyEnabled: boolean;
  isNewMobileUXActivated: boolean;
  isLandlordToolKitEnabled: boolean;
  isTenantToolkitEnabled: boolean;
  rpzAnnualRentCapPercentage: number;
  complianceRentReviewPeriod: number;

  // Add more config parameters as needed
}

type RemoteConfigKey =
  | 'isAttachmentEnabledInChat'
  | 'welcomeMessage'
  | 'isComingSoonEnabled'
  | 'maxListingsPerUser'
  | 'isFeedbackEnabled'
  | 'featuredListingIds'
  | 'requireEmailVerificationToCreateListing'
  | 'isGroupedListingEnabled'
  | 'isAutoApplyEnabled'
  | 'requireIdentityVerification'
  | 'isNewMobileUXActivated'
  | 'isLandlordToolKitEnabled'
  | 'isTenantToolkitEnabled'
  | 'rpzAnnualRentCapPercentage'
  | 'complianceRentReviewPeriod'
  | 'isHomeValuationToolEnabled'
  | 'complianceRules_small'
  | 'complianceRules_large';

const configDefaults: { [key in RemoteConfigKey]: string | number | boolean } =
  {
    isAttachmentEnabledInChat: false,
    welcomeMessage: 'Welcome to RentSmart.ie!',
    maxListingsPerUser: 10,
    isComingSoonEnabled: true,
    requireEmailVerificationToCreateListing: false,
    requireIdentityVerification: false,
    isGroupedListingEnabled: false,
    isFeedbackEnabled: true,
    isAutoApplyEnabled: true,
    isNewMobileUXActivated: true,
    featuredListingIds: '[]', // Store array as JSON string
    isLandlordToolKitEnabled: false,
    isTenantToolkitEnabled: false,
    rpzAnnualRentCapPercentage: 2, // Default 2% annual rent cap for RPZ compliance
    complianceRentReviewPeriod: 24, // Default 24 months for rent review
    isHomeValuationToolEnabled: true,
    // Compliance rules for small landlords (≤3 tenancies)
    complianceRules_small: JSON.stringify({
      category: 'Small Landlord',
      description: 'Applies to landlords with 3 or fewer tenancies',
      rtbRegistration: {
        required: true,
        frequency: 'Annual',
        deadline: '31 December each year',
        link: 'https://portal.rtb.ie/portal-login'
      },
      terminationGrounds: [
        { ground: 'Owner-occupation', noticePeriod: 90, requiresRtb: true },
        { ground: 'Sale of property', noticePeriod: 90, requiresRtb: true },
        { ground: 'Substantial breach', noticePeriod: 60, requiresRtb: false },
        { ground: 'Non-payment of rent', noticePeriod: 60, requiresRtb: false }
      ],
      rentReview: {
        eligible: true,
        minPeriod: 24,
        maxIncrease: '2% (RPZ cap)',
        noticeRequired: 90
      },
      depositProtection: {
        required: true,
        scheme: 'Registered scheme',
        interestReturn: true
      },
      inspectionRights: {
        allowed: true,
        frequency: 'Reasonable notice required',
        unreasonableDenial: 'Illegal'
      }
    }),
    // Compliance rules for large landlords (≥4 tenancies)
    complianceRules_large: JSON.stringify({
      category: 'Large Landlord',
      description: 'Applies to landlords with 4 or more tenancies',
      rtbRegistration: {
        required: true,
        frequency: 'Annual',
        deadline: '31 December each year',
        link: 'https://portal.rtb.ie/portal-login',
        additionalRequirements:
          'Must maintain up-to-date records for all tenancies'
      },
      terminationGrounds: [
        { ground: 'Owner-occupation', noticePeriod: 90, requiresRtb: true },
        { ground: 'Sale of property', noticePeriod: 90, requiresRtb: true },
        { ground: 'Substantial breach', noticePeriod: 60, requiresRtb: false },
        { ground: 'Non-payment of rent', noticePeriod: 60, requiresRtb: false },
        { ground: 'Redevelopment', noticePeriod: 180, requiresRtb: true }
      ],
      rentReview: {
        eligible: true,
        minPeriod: 24,
        maxIncrease: '2% (RPZ cap)',
        noticeRequired: 90,
        documentation: 'Must provide market evidence'
      },
      depositProtection: {
        required: true,
        scheme: 'Registered scheme',
        interestReturn: true,
        audit: 'Recommended annual audit'
      },
      inspectionRights: {
        allowed: true,
        frequency: 'Maximum twice per year',
        unreasonableDenial: 'Illegal',
        documentation: 'Must maintain inspection records'
      },
      additionalCompliance: [
        'Quarterly compliance review recommended',
        'Professional indemnity insurance advised',
        'Written tenancy policies required',
        'Regular staff training on regulations'
      ]
    })
  };

class RemoteConfigHelper {
  private static instance: RemoteConfigHelper;
  private remoteConfig = firebaseRemoteConfigApp;
  private isInitialized = false;
  private localOverrides: { [key: string]: string | boolean | number } = {};

  private constructor() {
    this.remoteConfig.defaultConfig = configDefaults;
    this.remoteConfig.settings = {
      fetchTimeoutMillis: 60000, // 1 minute
      minimumFetchIntervalMillis:
        process.env.NODE_ENV === 'development' ? 10000 : 3600000 // 10 seconds in dev, 1 hour in prod
    };
    this.initializeRemoteConfig();
  }

  public static getInstance(): RemoteConfigHelper {
    if (!RemoteConfigHelper.instance) {
      RemoteConfigHelper.instance = new RemoteConfigHelper();
    }
    return RemoteConfigHelper.instance;
  }

  private async initializeRemoteConfig(): Promise<void> {
    try {
      // Apply local overrides first in development
      const overrides = applyLocalOverrides();
      if (process.env.NODE_ENV === 'development') {
        this.localOverrides = overrides;
      }

      await fetchAndActivate(this.remoteConfig);
    } catch (error) {
      console.error('Remote Config initialization failed:', error);
    } finally {
      this.isInitialized = true;
    }
  }

  public async ensureInitialized(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    // This creates a promise that resolves when initialization is complete.
    return new Promise((resolve) => {
      const check = () => {
        if (this.isInitialized) {
          resolve();
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  // Boolean config accessors
  public isAttachmentEnabledInChat(): boolean {
    return this.getBooleanValue('isAttachmentEnabledInChat');
  }

  public requireEmailVerificationToCreateListing(): boolean {
    return this.getBooleanValue('requireEmailVerificationToCreateListing');
  }

  public requireIdentityVerification(): boolean {
    return this.getBooleanValue('requireIdentityVerification');
  }

  public isGroupedListingEnabled(): boolean {
    return this.getBooleanValue('isGroupedListingEnabled');
  }

  public isComingSoonEnabled(): boolean {
    return this.getBooleanValue('isComingSoonEnabled');
  }

  public isFeedbackEnabled(): boolean {
    return this.getBooleanValue('isFeedbackEnabled');
  }

  public isAutoApplyEnabled(): boolean {
    return this.getBooleanValue('isAutoApplyEnabled');
  }

  public isNewMobileUXActivated(): boolean {
    return this.getBooleanValue('isNewMobileUXActivated');
  }

  public isLandlordToolKitEnabled(): boolean {
    return this.getBooleanValue('isLandlordToolKitEnabled');
  }

  public isTenantToolkitEnabled(): boolean {
    return this.getBooleanValue('isTenantToolkitEnabled');
  }

  public isHomeValuationToolEnabled(): boolean {
    return getValue(
      this.remoteConfig,
      'isHomeValuationToolEnabled'
    ).asBoolean();
  }

  // String config accessors
  public getWelcomeMessage(): string {
    return this.getStringValue('welcomeMessage');
  }

  // Number config accessors
  public getMaxListingsPerUser(): number {
    return this.getNumberValue('maxListingsPerUser');
  }

  public getRpzAnnualRentCapPercentage(): number {
    return this.getNumberValue('rpzAnnualRentCapPercentage');
  }

  public getComplianceRentReviewPeriod(): number {
    return this.getNumberValue('complianceRentReviewPeriod');
  }

  // Generic accessors
  private getValue(
    key: keyof RemoteConfigParams
  ): Value | string | boolean | number {
    // In development, local overrides take top priority
    if (
      process.env.NODE_ENV === 'development' &&
      this.localOverrides[key] !== undefined
    ) {
      return this.localOverrides[key];
    }
    return getValue(this.remoteConfig, key);
  }

  private getBooleanValue(key: keyof RemoteConfigParams): boolean {
    const value = this.getValue(key);
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    // It's a Value object from Firebase
    return (value as Value).asBoolean();
  }

  private getStringValue(key: keyof RemoteConfigParams): string {
    const value = this.getValue(key);
    if (typeof value === 'string') return value;
    return (value as Value).asString();
  }

  private getNumberValue(key: keyof RemoteConfigParams): number {
    const value = this.getValue(key);
    if (typeof value === 'number') return value;
    return (value as Value).asNumber();
  }

  private getArrayValue(key: keyof RemoteConfigParams): string[] {
    const value = this.getValue(key);
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return [];
      }
    }
    const stringVal = (value as Value).asString();
    return stringVal ? JSON.parse(stringVal) : [];
  }

  // Compliance Engine Methods
  public getSmallLandlordRules(): string {
    return this.getStringValue('complianceRules_small' as any) || '{}';
  }

  public getLargeLandlordRules(): string {
    return this.getStringValue('complianceRules_large' as any) || '{}';
  }

  public getComplianceRulesBySize(
    landlordType: 'small' | 'large'
  ): Record<string, any> {
    try {
      const rulesStr =
        landlordType === 'small'
          ? this.getSmallLandlordRules()
          : this.getLargeLandlordRules();
      return JSON.parse(rulesStr);
    } catch {
      return {};
    }
  }
}

export const remoteConfig = RemoteConfigHelper.getInstance();
